<article (mouseenter)="hoverArmed.set(true)" (focusin)="hoverArmed.set(true)" class="group flex h-full flex-col rounded-[10px] border border-[#dbdbde] bg-white px-3 py-5 text-center transition hover:border-orange sm:px-4 sm:py-7">
  <!--
    Every row below is a fixed-height slot that renders whether or not its data exists, so a card
    with no rating/old price is exactly as tall as a fully-populated one. That is what keeps the
    grid uniform across pages that carry different amounts of product data.

    Media renders once with the link as a stretched overlay anchor — duplicating it per link-type
    branch (or pulling it in via ngTemplateOutlet) made the server and client DOM differ, which
    blanked the image on hydration.
  -->
  <div class="relative flex h-[130px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-[#fafafa] sm:h-[170px]">
    @if (showImage()) {
      <img [src]="image()" [alt]="title()" loading="lazy" (error)="imageFailed.set(true)" class="h-full w-full object-cover transition group-hover:scale-105" />
      <!--
        Alternate angle, cross-faded in on hover. Layered as a sibling with opacity rather than
        swapping [src], so the server and client render the same DOM and the browser has the second
        image decoded before the hover — swapping the source hitches on first hover.
      -->
      @if (showHoverImage()) {
        <img [src]="hoverImage()" [alt]="title()" aria-hidden="true" (error)="hoverImageFailed.set(true)" class="absolute inset-0 h-full w-full object-cover opacity-0 transition duration-300 group-hover:scale-105 group-hover:opacity-100 motion-reduce:transition-none" />
      }
    } @else {
      <span class="grid h-full w-full place-items-center rounded-[10px] bg-[#f6f2fa] text-3xl text-orange" aria-hidden="true">🛍️</span>
    }

    <!-- The store badge: its logo, or its name on white when it has none or the image fails. The
         name is a real wordmark, unlike the initial letter this used to avoid.

         Sized by `.sav-product-store-badge` in product-card.scss, NOT by Tailwind utilities: see
         the note there for why `h-10 w-10` on this element never applied. -->
    @if (storeName()) {
      <app-store-logo [logoUrl]="storeLogoUrl()" [name]="storeName()" [attr.title]="storeName()" class="sav-product-store-badge" />
    }

    @if (isExternal()) {
      <a [href]="externalLink()" target="_blank" rel="nofollow sponsored noopener" class="absolute inset-0" [attr.aria-label]="title()"></a>
    } @else if (href()) {
      <a [routerLink]="href()" class="absolute inset-0" [attr.aria-label]="title()"></a>
    }

  </div>

  <!-- Title slot -->
  @if (isExternal()) {
    <a [href]="externalLink()" target="_blank" rel="nofollow sponsored noopener" class="mt-4 line-clamp-2 min-h-[34px] text-[12px] font-medium leading-[17px] text-[#444] hover:text-orange sm:mt-6 sm:min-h-[36px] sm:text-[13px] sm:leading-[18px]">{{ title() }}</a>
  } @else if (href()) {
    <a [routerLink]="href()" class="mt-4 line-clamp-2 min-h-[34px] text-[12px] font-medium leading-[17px] text-[#444] hover:text-orange sm:mt-6 sm:min-h-[36px] sm:text-[13px] sm:leading-[18px]">{{ title() }}</a>
  } @else {
    <p class="mt-4 line-clamp-2 min-h-[34px] text-[12px] font-medium leading-[17px] text-[#444] sm:mt-6 sm:min-h-[36px] sm:text-[13px] sm:leading-[18px]">{{ title() }}</p>
  }

  <!-- Rating slot -->
  <div class="mt-2 flex min-h-[18px] items-center justify-center gap-1 text-[12px] sm:text-[13px]">
    <span class="tracking-[0.08em] text-orange">{{ stars() }}</span>
    @if (reviews() !== null) {
      <span class="text-[#777]">({{ reviews() }})</span>
    }
  </div>

  <!--
    Was / SAVE slot.

    Both halves are `whitespace-nowrap` and sized to fit one line inside the narrowest card the grid
    produces (5-across ≈ 205px of inner width). They used to inherit the same 13px and wrap freely,
    which put "SAVE GBP 23.82" on a second line on some cards and not others — so a row of cards
    ended up different heights, defeating the fixed-height slots everywhere else on this card.

    The saving is a pill rather than loose red bold text: it reads as a label at a glance and keeps
    its own shape at any width.

    The muted grey here and on the "Now" label below is #71717f, not the #8a8a94 it was. At 10.5px
    and 11.5px the old value sat at 3.41:1 against the card, under the 4.5:1 that small text needs;
    this one measures 4.8:1 and is close enough in tone that nothing about the card looks different.
  -->
  <div class="mt-3 flex min-h-[22px] flex-nowrap items-center justify-center gap-2">
    @if (hasPriceCompare()) {
      <!-- No "was" label: the strikethrough already says it, and those three characters were the
           difference between fitting on one line and not in the narrowest card. -->
      <s
        class="whitespace-nowrap text-[11.5px] leading-none text-[#71717f] decoration-[#c0bcc8] decoration-1 sm:text-[12px]"
        >{{ oldPrice() }}</s
      >

      @if (saveLabel()) {
        <span
          class="whitespace-nowrap rounded-full bg-[#fdeceb] px-2 py-[3px] text-[10px] font-extrabold uppercase leading-none tracking-[0.02em] text-[#c0392b] sm:text-[10.5px]"
          >{{ saveLabel() }}</span
        >
      }
    }
  </div>

  <!-- Price slot. `mt-auto` is what actually pins it: the card is a flex column stretched to the
       row's height, so the spare space collects above the price and every price in a row sits on the
       same line even when a title or savings label runs longer. -->
  <p class="mt-auto flex min-h-[26px] items-baseline justify-center gap-1.5 pt-3 sm:min-h-[30px]">
    @if (price()) {
      <!-- "NOW" labels the live price, pairing with the struck-through old price above. Small and
           muted so the number stays the thing you read first. -->
      <span class="text-[10.5px] font-bold uppercase tracking-[0.04em] text-[#71717f]">Now</span>
    }
    <span class="text-[22px] font-bold leading-none tracking-[-0.03em] text-[#333] sm:text-[26px]">{{
      price()
    }}</span>
  </p>

  <!--
    Actions. Text links rather than filled buttons, matching the "Get Deal" / "Reveal Code"
    treatment on the coupon cards.

    "Buy Now" leaves the site, so it opens in a new tab with `nofollow sponsored`. Its href is the
    `/go/p/:id` redirect whenever a product id is available, never the affiliate URL.
    "View Detail" is always in-app. This replaced a hover-only "View" overlay on the image: it
    duplicated View Detail, and a hover affordance is unreachable on touch.

    The row is a fixed-height slot like the others above, so a card with only one action (or none,
    on pages that pass neither link) stays the same height as its neighbours.
  -->
  <!-- `whitespace-nowrap` on the labels and a tighter gap: in a six-across row the card is ~168px
       wide (~144px inside its padding) while "Buy Now | View Detail" needs ~155px, so both labels
       broke mid-phrase onto two lines and the row stopped being the fixed-height slot every other
       row on this card is.

       Both labels also carry an `after:` overlay. As plain text they are only 20px tall, which is a
       poor target for a thumb; the pseudo-element grows what responds to a tap to ~38px while the
       type and the row height stay exactly as they are. It reaches 4px sideways, well inside the
       10px gap, so neither label can steal the other's tap.

       The `aria-label`s lead with the visible words. "Buy <product> now" read as one thing while
       the link showed another, so voice control had no way to act on what a person could see; the
       accessible name has to contain the visible text, and "Buy Now: <product>" does. -->
  <div
    class="mt-3 flex min-h-[20px] flex-wrap items-center justify-center gap-x-2.5 gap-y-1 text-[12px] font-bold sm:gap-x-3 sm:text-[13px]"
  >
    @if (buyLink()) {
      @if (isBuyExternal()) {
        <a
          [href]="buyLink()"
          target="_blank"
          rel="nofollow sponsored noopener"
          class="relative whitespace-nowrap text-orange underline-offset-2 after:absolute after:-inset-x-1 after:-inset-y-[9px] after:content-[''] hover:underline"
          [attr.aria-label]="'Buy Now: ' + title()"
          >Buy Now</a
        >
      } @else {
        <a
          [routerLink]="buyLink()"
          class="relative whitespace-nowrap text-orange underline-offset-2 after:absolute after:-inset-x-1 after:-inset-y-[9px] after:content-[''] hover:underline"
          [attr.aria-label]="'Buy Now: ' + title()"
          >Buy Now</a
        >
      }
    }

    <!-- Divider, only when both actions are present. A bordered span rather than a literal "|" so
         screen readers don't announce it and it can't be selected with the link text. -->
    @if (buyLink() && detailLink()) {
      <span class="h-3 w-px shrink-0 bg-[#d6d0dc]" aria-hidden="true"></span>
    }

    @if (detailLink()) {
      <a
        [routerLink]="detailLink()"
        class="relative whitespace-nowrap text-[#2d123f] underline-offset-2 after:absolute after:-inset-x-1 after:-inset-y-[9px] after:content-[''] hover:text-orange hover:underline"
        [attr.aria-label]="'View Detail: ' + title()"
        >View Detail</a
      >
    }
  </div>
</article>
